import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../../../ui/Navbar";
import Loader from "../../../ui/Loader";
import { useMaintenance } from "../../../../context/MaintenanceContext";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";
import MaintenancePage from "../../../ui/MaintenancePage";

const serverUrl = process.env.REACT_APP_SERVER_URL;

// Helper to convert Date to YYYY-MM-DDTHH:mm for datetime-local input
const toDatetimeLocal = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatReadable = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function ManageMaintenanceMode() {
  const { checkMaintenanceStatus, previewMode, setPreviewMode } =
    useMaintenance();

  const [activeConfig, setActiveConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "Scheduled Platform Maintenance",
    reason:
      "We are undergoing critical upgrades to improve performance and reliability",
    startDateTime: "",
    estimatedHours: 2,
    isActive: true,
  });

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", id: "home" },
    {
      name: "Maintenance Mode",
      path: "/superadmin/manage-maintenance",
      isLast: true,
      id: "manage-maintenance",
    },
  ];

  // Set default schedule dates: next 15 minutes with 2 hours estimated duration
  const initDefaultDates = useCallback(() => {
    const start = new Date();
    start.setMinutes(start.getMinutes() + 15);
    start.setSeconds(0);
    start.setMilliseconds(0);

    setFormData((prev) => ({
      ...prev,
      startDateTime: toDatetimeLocal(start),
      estimatedHours: 2,
    }));
  }, []);

  // Fetch current maintenance settings
  const fetchMaintenanceConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/maintenance/config`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response) return;
      const data = await response.json();

      if (response.ok) {
        setActiveConfig(data.active || null);
        setHistory(data.history || []);

        if (data.active) {
          setFormData({
            title: data.active.title || "Scheduled Platform Maintenance",
            reason: data.active.reason || "",
            startDateTime: toDatetimeLocal(data.active.startDateTime),
            estimatedHours: data.active.estimatedHours ?? 2,
            isActive: Boolean(data.active.isActive),
          });
        } else {
          initDefaultDates();
        }
      } else {
        swalError(data.message || "Failed to fetch maintenance configuration");
      }
    } catch (error) {
      swalError("Network error while retrieving maintenance configuration");
    } finally {
      setIsLoading(false);
    }
  }, [initDefaultDates]);

  useEffect(() => {
    fetchMaintenanceConfig();
  }, [fetchMaintenanceConfig]);

  // Handle Quick Presets
  const applyPreset = (presetType) => {
    const now = new Date();
    let start = new Date(now);
    let hours = 2;

    if (presetType === "now-1h") {
      hours = 1;
    } else if (presetType === "now-2h") {
      hours = 2;
    } else if (presetType === "now-4h") {
      hours = 4;
    } else if (presetType === "tonight") {
      start.setHours(23, 0, 0, 0);
      hours = 2;
    } else if (presetType === "tomorrow-night") {
      start.setDate(start.getDate() + 1);
      start.setHours(23, 0, 0, 0);
      hours = 2;
    }

    setFormData((prev) => ({
      ...prev,
      startDateTime: toDatetimeLocal(start),
      estimatedHours: hours,
    }));
  };

  // Submit new or updated maintenance schedule
  const handleSubmitSchedule = async (e) => {
    e.preventDefault();

    if (!formData.reason || !formData.reason.trim()) {
      swalError("Please enter a reason for the maintenance mode.");
      return;
    }

    if (!formData.startDateTime) {
      swalError("Please select a start date and time.");
      return;
    }

    const estHours = Number(formData.estimatedHours);
    if (isNaN(estHours) || estHours <= 0) {
      swalError("Please enter a valid estimated time in hours (e.g. 2).");
      return;
    }

    const start = new Date(formData.startDateTime);

    const confirmed = await swalConfirm(
      "Schedule Maintenance Window?",
      `Maintenance will start at ${formatReadable(start)} with an estimated duration of ${estHours} hr${estHours > 1 ? "s" : ""}. Non-superadmin users will be blocked during maintenance.`,
      "Confirm & Schedule",
      "Cancel",
    );

    if (!confirmed?.isConfirmed) return;

    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/maintenance/schedule`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: formData.title,
            reason: formData.reason,
            startDateTime: start.toISOString(),
            estimatedHours: estHours,
            isActive: true,
          }),
        },
      );

      if (!response) return;
      const data = await response.json();

      if (response.ok) {
        swalSuccess("Maintenance mode scheduled successfully!");
        fetchMaintenanceConfig();
        checkMaintenanceStatus();
      } else {
        swalError(data.message || "Failed to schedule maintenance");
      }
    } catch (error) {
      swalError("Error scheduling maintenance");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Immediate toggle (Emergency on/off)
  const handleEmergencyToggle = async (shouldActivate) => {
    if (shouldActivate) {
      const confirmed = await swalConfirm(
        "Activate Emergency Maintenance NOW?",
        "All student, instructor, and admin traffic will be blocked immediately. Estimated time is 2 hours. The maintenance page will appear for all active users.",
        "Yes, Activate Now",
        "Cancel",
      );
      if (!confirmed?.isConfirmed) return;

      const now = new Date();

      setIsSubmitting(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/maintenance/schedule`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: formData.title || "Emergency System Maintenance",
              reason:
                formData.reason ||
                "We are performing critical system repairs and updates.",
              startDateTime: now.toISOString(),
              estimatedHours: 2,
              isActive: true,
            }),
          },
        );

        if (response && response.ok) {
          swalSuccess("Maintenance is now ACTIVE platform-wide (Est. 2 hrs).");
          fetchMaintenanceConfig();
          checkMaintenanceStatus();
        }
      } catch (e) {
        swalError("Failed to activate emergency maintenance");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const confirmed = await swalConfirm(
        "Deactivate Maintenance Mode?",
        "This will restore full platform access for all users immediately.",
        "Yes, Restore Access",
        "Cancel",
      );
      if (!confirmed?.isConfirmed) return;

      if (!activeConfig?._id) return;

      setIsSubmitting(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/maintenance/toggle/${activeConfig._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ isActive: false }),
          },
        );

        if (response && response.ok) {
          swalSuccess(
            "Maintenance mode ended. Normal platform access restored!",
          );
          fetchMaintenanceConfig();
          checkMaintenanceStatus();
        }
      } catch (e) {
        swalError("Failed to deactivate maintenance mode");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Cancel / Delete a schedule
  const handleDeleteSchedule = async (id) => {
    const confirmed = await swalConfirm(
      "Cancel this maintenance schedule?",
      "This maintenance window will be cancelled and removed from the active schedule.",
      "Yes, Cancel Schedule",
      "Cancel",
    );
    if (!confirmed?.isConfirmed) return;

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/maintenance/${id}`,
        {
          method: "DELETE",
        },
      );

      if (response && response.ok) {
        swalSuccess("Schedule removed successfully");
        fetchMaintenanceConfig();
        checkMaintenanceStatus();
      }
    } catch (e) {
      swalError("Failed to remove schedule");
    }
  };

  // Calculate live state of activeConfig
  const now = new Date();
  const isCurrentlyInWindow =
    activeConfig?.isActive &&
    now >= new Date(activeConfig.startDateTime);
  const isScheduledFuture =
    activeConfig?.isActive && now < new Date(activeConfig.startDateTime);

  return (
    <div className="super-admin-container">
      <Navbar
        pageName="GradeMe-AI Super Admin Panel"
        breadcrumbs={breadcrumbs}
      />

      {/* Live Preview Modal */}
      {previewMode && (
        <div className="maintenance-fullpage-preview-container">
          <MaintenancePage />
        </div>
      )}

      <div className="sa-home-content manage-maintenance-wrapper">
        {/* Status Dashboard Header */}
        <div className="maintenance-sa-header">
          <div className="maintenance-sa-header-text">
            <h2>System Maintenance Management</h2>
            <p>
              Schedule platform maintenance windows, broadcast advance
              notifications to users, or instantly toggle emergency maintenance
              lockdown.
            </p>
          </div>

          <div className="maintenance-sa-header-actions">
            <button
              type="button"
              className="maintenance-sa-preview-btn"
              onClick={() => setPreviewMode(true)}
              title="Preview how users see the maintenance screen"
            >
              <i className="fa-solid fa-eye"></i>
              <span>Preview Maintenance Page</span>
            </button>
          </div>
        </div>

        {/* Current State Indicator Box */}
        <div
          className={`maintenance-state-banner ${
            isCurrentlyInWindow
              ? "state-active"
              : isScheduledFuture
                ? "state-scheduled"
                : "state-normal"
          }`}
        >
          <div className="state-banner-left">
            <div className="state-status-indicator">
              <span className="state-pulse-ball"></span>
              <span className="state-title-text">
                {isCurrentlyInWindow
                  ? "ACTIVE: PLATFORM CURRENTLY IN MAINTENANCE"
                  : isScheduledFuture
                    ? "SCHEDULED: UPCOMING MAINTENANCE SET"
                    : "OPERATIONAL: PLATFORM OPERATING NORMALLY"}
              </span>
            </div>

            <p className="state-banner-description">
              {isCurrentlyInWindow ? (
                <>
                  Non-superadmin users calling any API receive 503 and see the
                  Maintenance Page. Started at:{" "}
                  <strong>{formatReadable(activeConfig.startDateTime)}</strong>{" "}
                  (Estimated Duration:{" "}
                  <strong>
                    ~{activeConfig.estimatedHours || 2} hr
                    {(activeConfig.estimatedHours || 2) > 1 ? "s" : ""}
                  </strong>
                  ).
                </>
              ) : isScheduledFuture ? (
                <>
                  Advance notice is active above user navigation bars. Starts
                  at:{" "}
                  <strong>{formatReadable(activeConfig.startDateTime)}</strong>{" "}
                  (Estimated Duration:{" "}
                  <strong>
                    ~{activeConfig.estimatedHours || 2} hr
                    {(activeConfig.estimatedHours || 2) > 1 ? "s" : ""}
                  </strong>
                  ).
                </>
              ) : (
                "All platform features, examinations, and AI evaluation engines are running normally."
              )}
            </p>
          </div>

          <div className="state-banner-right">
            {isCurrentlyInWindow ? (
              <button
                type="button"
                className="maintenance-emergency-off-btn"
                onClick={() => handleEmergencyToggle(false)}
                disabled={isSubmitting}
              >
                <i className="fa-solid fa-power-off"></i>
                <span>End Maintenance Now</span>
              </button>
            ) : (
              <button
                type="button"
                className="maintenance-emergency-on-btn"
                onClick={() => handleEmergencyToggle(true)}
                disabled={isSubmitting}
              >
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>Put on Maintenance Mode Now (Est. 2 hrs)</span>
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="maintenance-loading-box">
            <Loader />
          </div>
        ) : (
          <div className="maintenance-layout-grid">
            {/* Left Column: Scheduling Form */}
            <div className="maintenance-card-panel">
              <div className="maintenance-panel-header">
                <div className="panel-header-icon color-amber">
                  <i className="fa-solid fa-calendar-plus"></i>
                </div>
                <div>
                  <h3>Configure & Schedule Maintenance Window</h3>
                  <p>
                    Define the exact maintenance period and explanation shown to
                    users.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleSubmitSchedule}
                className="maintenance-form"
              >
                {/* Presets */}
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">
                    <i className="fa-solid fa-bolt"></i> Quick Estimated Time Presets
                  </label>
                  <div className="maintenance-presets-row">
                    <button
                      type="button"
                      className={`maintenance-preset-chip ${Number(formData.estimatedHours) === 1 ? "active" : ""}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, estimatedHours: 1 }))
                      }
                    >
                      1 hr
                    </button>
                    <button
                      type="button"
                      className={`maintenance-preset-chip ${Number(formData.estimatedHours) === 2 ? "active" : ""}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, estimatedHours: 2 }))
                      }
                    >
                      2 hrs
                    </button>
                    <button
                      type="button"
                      className={`maintenance-preset-chip ${Number(formData.estimatedHours) === 3 ? "active" : ""}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, estimatedHours: 3 }))
                      }
                    >
                      3 hrs
                    </button>
                    <button
                      type="button"
                      className={`maintenance-preset-chip ${Number(formData.estimatedHours) === 4 ? "active" : ""}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, estimatedHours: 4 }))
                      }
                    >
                      4 hrs
                    </button>
                    <button
                      type="button"
                      className="maintenance-preset-chip"
                      onClick={() => applyPreset("tonight")}
                    >
                      Tonight (11PM, 2h)
                    </button>
                    <button
                      type="button"
                      className="maintenance-preset-chip"
                      onClick={() => applyPreset("tomorrow-night")}
                    >
                      Tomorrow (11PM, 2h)
                    </button>
                  </div>
                </div>

                {/* Date-time Pickers */}
                <div className="maintenance-form-row">
                  <div className="maintenance-form-group">
                    <label
                      className="maintenance-form-label"
                      htmlFor="startDateTime"
                    >
                      <i className="fa-regular fa-clock"></i> Start Date & Time
                      *
                    </label>
                    <input
                      type="datetime-local"
                      id="startDateTime"
                      name="startDateTime"
                      className="maintenance-input"
                      value={formData.startDateTime}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          startDateTime: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="maintenance-form-group">
                    <label
                      className="maintenance-form-label"
                      htmlFor="estimatedHours"
                    >
                      <i className="fa-solid fa-hourglass-half"></i> Estimated
                      Time (in hours) *
                    </label>
                    <input
                      type="number"
                      id="estimatedHours"
                      name="estimatedHours"
                      className="maintenance-input"
                      min="0.5"
                      step="0.5"
                      placeholder="e.g. 2"
                      value={formData.estimatedHours}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          estimatedHours: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {/* Title */}
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="title">
                    <i className="fa-solid fa-heading"></i> Notice Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className="maintenance-input"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g. Scheduled Platform Maintenance"
                  />
                </div>

                {/* Reason */}
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="reason">
                    <i className="fa-solid fa-align-left"></i> Reason for
                    Maintenance (Shown on Maintenance Page) *
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    rows="4"
                    className="maintenance-textarea"
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    placeholder="Describe why maintenance is being done (e.g. Upgrading AI scoring models, database optimization, or server migration)."
                    required
                  ></textarea>
                  <span className="maintenance-form-hint">
                    This reason is displayed prominently on the maintenance
                    screen to reassure users.
                  </span>
                </div>

                {/* Submit button */}
                <div className="maintenance-form-actions">
                  <button
                    type="submit"
                    className="maintenance-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                        <span>Saving Schedule...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-calendar-check"></i>
                        <span>Save & Schedule Maintenance Window</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Live Configuration Summary & Past Windows */}
            <div className="maintenance-card-panel">
              <div className="maintenance-panel-header">
                <div className="panel-header-icon color-berry">
                  <i className="fa-solid fa-list-check"></i>
                </div>
                <div>
                  <h3>Active Schedule & History</h3>
                  <p>
                    Review current active plan and audit past maintenance
                    records.
                  </p>
                </div>
              </div>

              {/* Current Active Plan Card */}
              <div className="active-plan-summary-box">
                <div className="active-plan-header">
                  <span className="active-plan-tag">
                    {activeConfig?.isActive
                      ? "Active Configuration"
                      : "No Active Schedule"}
                  </span>
                  {activeConfig?.isActive && (
                    <button
                      type="button"
                      className="active-plan-cancel-btn"
                      onClick={() => handleDeleteSchedule(activeConfig._id)}
                      title="Cancel this schedule"
                    >
                      <i className="fa-solid fa-trash-can"></i>
                      <span>Cancel</span>
                    </button>
                  )}
                </div>

                {activeConfig?.isActive ? (
                  <div className="active-plan-body">
                    <h4 className="active-plan-title">{activeConfig.title}</h4>
                    <p className="active-plan-reason">{activeConfig.reason}</p>
                    <div className="active-plan-dates">
                      <div className="plan-date-item">
                        <span className="plan-date-lbl">Starts:</span>
                        <span className="plan-date-val">
                          {formatReadable(activeConfig.startDateTime)}
                        </span>
                      </div>
                      <div className="plan-date-item">
                        <span className="plan-date-lbl">Estimated Duration:</span>
                        <span className="plan-date-val">
                          ~{activeConfig.estimatedHours || 2} hr
                          {(activeConfig.estimatedHours || 2) > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-active-plan">
                    <i className="fa-solid fa-calendar-xmark"></i>
                    <p>No maintenance window currently active or scheduled.</p>
                  </div>
                )}
              </div>

              {/* Past Maintenance Logs */}
              <div className="maintenance-history-section">
                <h4 className="history-section-title">
                  <i className="fa-solid fa-clock-rotate-left"></i> Maintenance
                  History (Past 25)
                </h4>

                {history.length === 0 ? (
                  <div className="history-empty">
                    No maintenance records logged yet.
                  </div>
                ) : (
                  <div className="history-list">
                    {history.map((item) => {
                      return (
                        <div key={item._id} className="history-item">
                          <div className="history-item-top">
                            <span
                              className={`history-badge ${
                                item.isActive
                                  ? "badge-current"
                                  : "badge-past"
                              }`}
                            >
                              {item.isActive
                                ? "Active"
                                : "Completed / Archived"}
                            </span>
                            <span className="history-date-range">
                              Started: {formatReadable(item.startDateTime)} · Est. ~{item.estimatedHours || 2} hr{(item.estimatedHours || 2) > 1 ? "s" : ""}
                            </span>
                          </div>

                          <p className="history-reason">{item.reason}</p>

                          <div className="history-item-actions">
                            <button
                              type="button"
                              className="history-delete-btn"
                              onClick={() => handleDeleteSchedule(item._id)}
                              title="Delete record"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
